import type {Actions} from './createActions';
import type {ActionTypes} from './createActionTypes';
import {all, call, fork, put, select, takeEvery} from 'redux-saga/effects';
import {apiClient} from '../../../shared/api';
import {api} from '../../../shared/constants/api';
import {notifyError, notifySuccess} from '../../../shared/notificationUtils';
import pluralize from 'pluralize';
import {endsWith} from 'ramda';
import {formatDate} from "../../../modules/ui/components/AppMenu/redux/utils";
import {HttpStatus} from '../../../shared/constants/httpStatusCode';
import {getLastPath, normalizeResponse, getValueFromStateOrPayloadAction} from '../helper'

export function createCoreSagas(actions) {
    const watches = actions.map(action => {
        function* watch() {
            yield takeEvery(action.type, action.callback);
        }

        return watch;
    });

    function* saga() {
        const forks = watches.map(watch => fork(watch));
        yield all(forks);
    }

    return saga;
}

export default function createSagas(actions: Actions, actionTypes: ActionTypes, entity: string) {
    // SHOULD REFACTOR: AUTO COMBINE REDUCER
    const getState = (state) => {
        for (let m in state) {
            if (m.toUpperCase() === getLastPath(entity).replace(/-/g, '').toUpperCase()) {
                return state[m];
            }
        }
    };

    function* get({payload: {id}}) {
        let response = yield call(apiClient.get, entity, id);
        response = normalizeResponse(response);
        if (response.error) {
            yield put(actions.getFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.getSuccess(response));
        }
    }

    function* listFirstTime(payload) {
        const state = yield select(getState);
        if (!state.listFirstTime) {
            yield put(actions.list(payload));
        }
    }

    function* list({payload: {page, sizePerPage, searchText, sort, filters, url, customParams}}) {
        if (!url) {
            url = entity
            if (!endsWith('/', entity)) {
                url = url + '/';
            }
        }

        let state = yield select(getState);

        if (state) {
            page = page || state.page;
            sizePerPage = sizePerPage || state.sizePerPage;
            searchText = !!searchText ? state.searchText : searchText;
            sort = getValueFromStateOrPayloadAction('sort', state, sort)
            filters = getValueFromStateOrPayloadAction('filters', state, filters)
            customParams = getValueFromStateOrPayloadAction('customParams', state, customParams)
        }

        let response = yield call(apiClient.list, url, page, sizePerPage, searchText, sort, filters, customParams);
        if (response.error) {
            yield put(actions.listFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            const total = parseInt(response.headers['x-total-items']);
            response = response.data;

            if (page > 1 && (!response || response.length === 0)) {
                yield put(actions.list(page - 1, sizePerPage, searchText, sort, filters, url, customParams));
            } else {
                response.forEach(normalizeResponse);
                yield put(actions.listSuccess(response, page, sizePerPage, searchText, sort, filters, total));

            }
        }
    }

    function* create({payload}) {
        const response = yield call(apiClient.create, entity, payload);

        if (response.error) {
            yield put(actions.createFailed(response.detail));
            yield call(notifyError, response.detail ? response.detail : 'An error has occurred');
        } else {
            yield put(actions.createSuccess(response));
            yield call(notifySuccess, 'Successfully created');

            yield put(actions.list());
        }
    }

    function* update({payload}) {
        let response;
        if (Array.isArray(payload)) {
            response = yield call(apiClient.updateBatch, entity, payload);
        } else {
            response = yield call(apiClient.update, entity, payload['id'], payload['data']);
        }

        if (response.error) {
            let message = response.detail.response.data.detail;
            if (message) {
                yield put(actions.updateFailed(response.detail));
            } else {
                yield put(actions.updateFailed(response.detail));
            }
            message = message ? message : 'An error has occurred';
            yield call(notifyError, message);
        } else {
            yield put(actions.updateSuccess(response.data));
            yield call(notifySuccess, 'Successfully updated');

            yield put(actions.list());
        }
    }

    function* show({payload}) {
        const response = yield call(apiClient.show, entity, payload['id']);

        if (response.error) {
            const result = normalizeResponse(response.detail);
            yield put(actions.updateFailed(result));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.updateSuccess(response));
        }
    }

    function* deleteResource({payload}) {
        const options = payload.options
        const items = payload.items || payload
        const response = yield call(apiClient.deleteBatch, entity, items, options);

        if (response.error) {
            yield put(actions.deleteFailed(response.detail));
            let message = 'An error has occurred';
            const responseData = response.detail.response.data;
            if (responseData && responseData.detail) {
                message = responseData.detail;
            }
            yield call(notifyError, message);
        } else {
            if (response.status === HttpStatus.MultiStatus && response.data) {
                yield put(actions.deleteSuccess(response));
                if (response.data.success) {
                    yield call(notifySuccess, response.data.success);
                }
                if (response.data.failed) {
                    yield call(notifyError, response.data.failed);
                }
            } else {
                yield put(actions.deleteSuccess(response));
                yield call(notifySuccess, `Successfully deleted ${pluralize('item', items.length, true)}`);
            }

            yield put(actions.list());
        }
    }

    function* exportResource({payload: {filters}}) {
        const response = yield call(apiClient.download, `${entity}/export`, filters);

        if (response.error) {
            yield put(actions.exportFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.exportSuccess(response));
        }
    }

    function* exportBigData({payload: {key, filters, zip}}) {
        const response = yield call(
            apiClient.export,
            `${entity}/${zip ? 'export-detail' : 'export'}?key=${key}`,
            filters);

        if (response.error) {
            yield put(actions.exportBigDataFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.exportBigDataSuccess(response.data));
        }
    }

    function* downloadFromURL({payload: {url}}) {
        try {
            const link = document.createElement('a');
            link.href = url;
            const fileName = (url) => {
                let urlPaths = url.split('/')
                return urlPaths.length > 0 ? urlPaths(urlPaths.length - 1) : ''
            }
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            yield put(actions.downloadFromURLSuccess());
        } catch (error) {
            yield put(actions.downloadFromURLFailed(error));
            yield call(notifyError, 'An error has occurred');
        }
    }

    function* exportDetail({payload: {filters}}) {
        const response = yield call(apiClient.download, `${entity}/export-detail`, filters);

        if (response.error) {
            yield put(actions.exportDetailFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.exportDetailSuccess(response));
        }
    }

    function* upload({payload}) {
        const {file} = payload;
        const formData = new FormData();
        formData.append('file', file, file.name);
        const response = yield call(apiClient.post, `${entity}/upload`, formData, {});

        if (response.error) {
            yield put(actions.uploadFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.uploadSuccess(response));
            yield call(notifySuccess, 'Successfully updated documents');
        }
    }

    function* downloadTemplate({payload}) {
        const response = yield call(apiClient.download, `${entity}/`, {'search_term': payload});

        if (response.error) {
            yield put(actions.downloadTemplateFailed(response.detail));
            yield call(notifyError, 'An error has occurred');
        } else {
            yield put(actions.downloadTemplateSuccess(response.data));
            yield call(notifySuccess, 'Successfully downloaded mass update template');
        }
    }

    function* watchGet(): any {
        yield takeEvery(actionTypes.GET, get);
    }

    function* watchListFirstTime(): any {
        yield takeEvery(actionTypes.LIST_FIRST_TIME, listFirstTime);
    }

    function* watchList(): any {
        yield takeEvery(actionTypes.LIST, list);
    }

    function* watchCreate(): any {
        yield takeEvery(actionTypes.CREATE, create);
    }

    function* watchUpdate(): any {
        yield takeEvery(actionTypes.UPDATE, update);
    }

    function* watchDelete(): any {
        yield takeEvery(actionTypes.DELETE, deleteResource);
    }

    function* watchShow(): any {
        yield takeEvery(actionTypes.SHOW, show);
    }

    function* watchExport(): any {
        yield takeEvery(actionTypes.EXPORT, exportResource);
    }

    function* watchExportBigData(): any {
        yield takeEvery(actionTypes.EXPORT_BIG_DATA, exportBigData);
    }

    function* watchDownloadFromURL(): any {
        yield takeEvery(actionTypes.DOWNLOAD_FROM_URL, downloadFromURL);
    }

    function* watchExportDetail(): any {
        yield takeEvery(actionTypes.EXPORT_DETAIL, exportDetail);
    }

    function* watchUpload(): any {
        yield takeEvery(actionTypes.UPLOAD, upload);
    }

    function* watchDownloadTemplate(): any {
        yield takeEvery(actionTypes.DOWNLOAD_TEMPLATE, downloadTemplate);
    }


    function* saga(): any {
        yield all([
            fork(watchGet), fork(watchListFirstTime), fork(watchList), fork(watchCreate),
            fork(watchUpdate), fork(watchDelete), fork(watchShow),
            fork(watchExport), fork(watchExportBigData), fork(watchDownloadFromURL), fork(watchExportDetail),
            fork(watchUpload),
            fork(watchDownloadTemplate),
        ]);
    }

    return saga;
}
